import { Button, Skeleton, Tabs } from '@mantine/core';
import {
  IconArrowBackUp,
  IconBuildingStore,
  IconCheck,
  IconCircleCheck,
  IconDownload,
  IconEdit,
  IconLock,
  IconPackageImport,
  IconPrinter,
  IconSend,
  IconX,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import { browserPdfOutput, renderPdf } from '../personnel/pdf.js';
import { errorCode } from '../personnel/source.js';
import { ItemsTotalRow } from './ItemsTotalRow.js';
import orders from './Orders.module.css';
import {
  historyDetail,
  markSentOffered,
  orderAmounts,
  orderWarnings,
  type PoTab,
  pricesVisible,
  sendConfirmable,
  sendState,
} from './orders.js';
import { PasswordDialog } from './PasswordDialog.js';
import { ConfirmDialog, DeliveryDialog } from './PurchaseOrderDialogs.js';
import { PurchaseOrderStatusBadge } from './PurchaseOrderList.js';
import { PurchaseOrderTotals } from './PurchaseOrderTotals.js';
import classes from './Purchasing.module.css';
import { type PdfT, purchaseOrderPdf, purchaseOrderPdfFileName } from './po-pdf.js';
import { departmentLabel, formatAmount, formatQuantity, formatStampLocal } from './requisitions.js';
import type { PurchaseOrderSource } from './source.js';
import { LoadError } from './table.js';
import type { PurchaseOrderDetail } from './types.js';

type Dialog =
  | 'submit'
  | 'approve'
  | 'return'
  | 'send'
  | 'markSent'
  | 'confirm'
  | 'cancel'
  | 'close'
  | 'edit'
  | null;

/**
 * Sipariş kartı (`satin-alma-akisi.jpeg` "Purchase Order"; ADR 0040): numara, durum rozeti ve
 * sipariş günü; Supplier / Currency / Total Amount | Expected Delivery kutuları; Print / Download /
 * Send to Supplier; sekmeler Items (kalemler ve toplam), Delivery Details, Receipts, History.
 * Ofiste yazılır (onaya gönderme, onay ve iade parolayla; dört göz); gemide salt okunur ve
 * "Receive (GRN)" mal kabulü formunu açar (ADR 0026 §1).
 */
export function PurchaseOrderDetailView({
  source,
  id,
  tab,
  message,
  onTab,
  onBack,
  onOpenRequisition,
  onOpenRfq,
  onOpenReceipt,
  onReceive,
}: {
  source: PurchaseOrderSource;
  id: string;
  tab: PoTab;
  message?: string | null;
  onTab: (tab: PoTab) => void;
  onBack: () => void;
  onOpenRequisition?: (requisitionId: string) => void;
  onOpenRfq?: (rfqId: string) => void;
  onOpenReceipt?: (goodsReceiptId: string) => void;
  /** Gemide: mal kabulü formu bu siparişle açılır. */
  onReceive?: (purchaseOrderId: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`purchasing.po.detail.${key}`, options);
  const [detail, setDetail] = useState<PurchaseOrderDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(message ?? null);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [pdf, setPdf] = useState<{ busy: 'save' | 'print' | null; error: string | null }>({
    busy: null,
    error: null,
  });

  const load = useCallback(async () => {
    setError(null);
    try {
      setDetail(await source.get(id));
    } catch (failure) {
      setError(errorCode(failure));
    }
  }, [source, id]);
  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <div className={classes.screen}>
        <PageHeader title={k('title')} />
        <LoadError title={k('error')} code={error} onRetry={() => void load()} />
      </div>
    );
  }
  if (!detail) {
    return (
      <div className={classes.screen} aria-busy>
        <span className={classes.visuallyHidden}>{k('loading')}</span>
        <PageHeader title={k('title')} />
        {/* Düzenin şeklinde iskelet: başlık ve kutular, sekmeler ve kalemler (M6f §0.6) */}
        <Skeleton height={150} />
        <Skeleton height={260} />
      </div>
    );
  }

  const editor = source.editor;
  const can = detail.can;
  const money = (value: number) => formatAmount(value, locale);
  // Gemi tedarikçi adını görür, fiyatı görmez (ADR 0169 §2): eski gemi sunucusu fiyat gönderse de
  const prices = pricesVisible(source.scope);
  const amounts = orderAmounts(detail);
  const hiddenNote = prices ? null : (
    <p className={classes.note}>{t('purchasingShip.prices.hidden')}</p>
  );
  const run = async (
    action: () => Promise<PurchaseOrderDetail>,
    done: (next: PurchaseOrderDetail) => string,
  ) => {
    try {
      const next = await action();
      setDetail(next);
      setDialog(null);
      setNotice(done(next));
      return null;
    } catch (failure) {
      return errorCode(failure);
    }
  };
  const exportPdf = (kind: 'save' | 'print') => {
    const output = source.pdf ?? browserPdfOutput();
    // Tedarikçiye giden belge İngilizce (ADR 0040 #181); worker'ın e-posta eki de öyle
    const english = i18n.getFixedT('en');
    const pdfT: PdfT = (key, options) => english(key, options ?? {});
    const fileName = purchaseOrderPdfFileName(detail.poNo, pdfT);
    const render = async () => {
      const company = { name: (await source.brandName?.()) ?? null };
      // Gemide fiyatsız kopya (ADR 0169 §2); ofisin PDF'i değişmez
      const definition = purchaseOrderPdf(
        {
          order: detail,
          company,
          generatedAt: new Date().toISOString(),
          ...(prices ? {} : { prices: false }),
        },
        pdfT,
      );
      return renderPdf(definition, { print: kind === 'print' });
    };
    setPdf({ busy: kind, error: null });
    (kind === 'save' ? output.save(fileName, render) : output.print(fileName, render))
      .then(() => setPdf({ busy: null, error: null }))
      .catch((failure: unknown) => setPdf({ busy: null, error: errorCode(failure) }));
  };
  // E-posta ayarı yoksa "Send to Supplier" yerine "Mark as Sent" (ADR 0040 #181)
  const mailOff = detail.mailEnabled === false;
  // Bekleyen e-posta varken düğme yerine not (M12c-R3)
  const send = sendState(detail);
  const sendable = Boolean(editor) && send === 'send';
  const hasReceipts = detail.receipts.length > 0;
  const validReceipts = detail.receipts.filter((r) => !r.cancelled).length;
  const showReceived = detail.lines.some((line) => line.received > 0);
  const rfq = detail.rfq;

  return (
    <div className={classes.screen}>
      <PageHeader
        title={k('title')}
        description={k('description')}
        crumb={detail.poNo}
        actions={
          editor && (can.edit || can.confirm || can.cancel || can.close) ? (
            <div className={classes.headerActions}>
              {can.edit ? (
                <Button
                  variant="default"
                  leftSection={<IconEdit size={16} />}
                  onClick={() => setDialog('edit')}
                >
                  {k('edit')}
                </Button>
              ) : null}
              {can.confirm ? (
                <Button
                  variant="default"
                  leftSection={<IconCheck size={16} />}
                  onClick={() => setDialog('confirm')}
                >
                  {k('confirm')}
                </Button>
              ) : null}
              {can.close ? (
                <Button
                  variant="default"
                  leftSection={<IconLock size={16} />}
                  onClick={() => setDialog('close')}
                >
                  {k('close')}
                </Button>
              ) : null}
              {can.cancel ? (
                <Button
                  variant="default"
                  leftSection={<IconX size={16} />}
                  onClick={() => setDialog('cancel')}
                >
                  {k('cancel')}
                </Button>
              ) : null}
            </div>
          ) : undefined
        }
      />

      {notice ? (
        <p className={classes.notice} data-tone="success" role="status">
          {notice}
        </p>
      ) : null}
      {detail.status === 'draft' && editor ? (
        <p className={classes.notice} data-tone="info">
          {k('draftNote')}
        </p>
      ) : null}
      {detail.status === 'pending_approval' && editor && !can.decide ? (
        <p className={classes.notice} data-tone="info">
          {k('pendingNote')}
        </p>
      ) : null}
      {detail.cancelledAt ? (
        <p className={classes.notice} data-tone="danger">
          {k('cancelledNote', { reason: detail.cancelReason ?? '' })}
        </p>
      ) : null}
      {detail.closedAt ? (
        <p className={classes.notice} data-tone="info">
          {detail.closeReason
            ? k('closedNote', { reason: detail.closeReason })
            : k('closedNoReason')}
        </p>
      ) : null}
      {detail.receivedAfterCancel ? (
        <p className={classes.notice} data-tone="danger" role="alert">
          {k('receivedAfterCancel', { count: validReceipts })}
        </p>
      ) : null}
      {orderWarnings(detail).includes('receivedAfterClose') ? (
        <p className={classes.notice} data-tone="danger" role="alert">
          {k('receivedAfterClose', { count: detail.receivedAfterClose ?? 0 })}
        </p>
      ) : null}
      {editor && mailOff && can.markSent ? (
        <p className={classes.notice} data-tone="info">
          {k('mailOff')}
        </p>
      ) : null}
      {editor && send === 'pending' ? (
        <p className={classes.notice} data-tone="info" role="status">
          {k('emailPending')}
        </p>
      ) : null}
      {pdf.error ? (
        <p className={classes.notice} data-tone="danger" role="alert">
          {k('pdfError')}
        </p>
      ) : null}

      <Panel aria-label={k('title')}>
        <div className={orders.head}>
          <div className={orders.headTitle}>
            <h2>{detail.poNo}</h2>
            <PurchaseOrderStatusBadge status={detail.status} />
          </div>
          <span className={orders.headDate}>{formatDay(detail.orderDate)}</span>
        </div>

        <div className={orders.cards}>
          <div className={orders.card}>
            <span className={orders.cardLabel}>{k('supplier')}</span>
            <span className={orders.cardValue} data-link>
              <IconBuildingStore size={16} aria-hidden />
              {detail.supplier.name}
            </span>
          </div>
          {prices ? (
            <div className={orders.card}>
              <span className={orders.cardLabel}>{k('currency')}</span>
              <span className={orders.cardValue}>{detail.currency}</span>
            </div>
          ) : null}
          {prices ? (
            <div className={orders.card} data-split>
              <div>
                <span className={orders.cardLabel}>{k('totalAmount')}</span>
                <span className={orders.cardValue}>{money(detail.total)}</span>
                {/* Short close (D8, ADR 0167 §1): sipariş tutarı ayrıca */}
                {amounts.shortClosed ? (
                  <span className={orders.cardLabel}>
                    {k('orderedNote', { amount: money(amounts.ordered) })}
                  </span>
                ) : null}
              </div>
              <div>
                <span className={orders.cardLabel}>{k('expectedDelivery')}</span>
                <span className={orders.cardValue}>{formatDay(detail.expectedDelivery)}</span>
              </div>
            </div>
          ) : (
            <div className={orders.card}>
              <span className={orders.cardLabel}>{k('expectedDelivery')}</span>
              <span className={orders.cardValue}>{formatDay(detail.expectedDelivery)}</span>
            </div>
          )}
        </div>

        <div className={orders.actions}>
          <Button
            variant="default"
            leftSection={<IconPrinter size={16} />}
            loading={pdf.busy === 'print'}
            onClick={() => exportPdf('print')}
          >
            {k('print')}
          </Button>
          <Button
            variant="default"
            leftSection={<IconDownload size={16} />}
            loading={pdf.busy === 'save'}
            onClick={() => exportPdf('save')}
          >
            {k('download')}
          </Button>
          {editor && can.submit ? (
            <Button leftSection={<IconSend size={16} />} onClick={() => setDialog('submit')}>
              {k('submit')}
            </Button>
          ) : null}
          {editor && can.decide ? (
            <>
              <Button
                variant="default"
                leftSection={<IconArrowBackUp size={16} />}
                onClick={() => setDialog('return')}
              >
                {k('return')}
              </Button>
              <Button
                leftSection={<IconCircleCheck size={16} />}
                onClick={() => setDialog('approve')}
              >
                {k('approve')}
              </Button>
            </>
          ) : null}
          {editor && markSentOffered(detail) ? (
            <Button leftSection={<IconCheck size={16} />} onClick={() => setDialog('markSent')}>
              {k('markSent')}
            </Button>
          ) : null}
          {sendable ? (
            <Button
              leftSection={<IconSend size={16} />}
              variant={detail.status === 'issued' ? 'filled' : 'default'}
              onClick={() => setDialog('send')}
            >
              {detail.status === 'issued' ? k('send') : k('resend')}
            </Button>
          ) : null}
          {onReceive && can.receive ? (
            <Button
              leftSection={<IconPackageImport size={16} />}
              onClick={() => onReceive(detail.id)}
            >
              {k('receive')}
            </Button>
          ) : null}
        </div>

        <Tabs
          value={tab}
          onChange={(value) => onTab((value ?? 'items') as PoTab)}
          className={classes.tabs}
          keepMounted={false}
        >
          <Tabs.List aria-label={k('tabsLabel')}>
            <Tabs.Tab value="items">{k('tabs.items')}</Tabs.Tab>
            <Tabs.Tab value="delivery">{k('tabs.delivery')}</Tabs.Tab>
            <Tabs.Tab value="receipts">
              {k('tabs.receipts', { count: detail.receipts.length })}
            </Tabs.Tab>
            <Tabs.Tab value="history">{k('tabs.history')}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="items" className={classes.tabBody}>
            <TableBox label={k('tabs.items')}>
              <thead>
                <tr>
                  <th scope="col" className={classes.index}>
                    {k('columns.no')}
                  </th>
                  <th scope="col" className={classes.key}>
                    {k('columns.material')}
                  </th>
                  <th scope="col">{k('columns.qty')}</th>
                  <th scope="col">{k('columns.uom')}</th>
                  {prices ? (
                    <>
                      <th scope="col">{k('columns.unitPrice')}</th>
                      <th scope="col">{k('columns.total')}</th>
                    </>
                  ) : null}
                  {showReceived ? (
                    <>
                      <th scope="col">{k('columns.received')}</th>
                      <th scope="col">{k('columns.open')}</th>
                    </>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {detail.lines.map((line) => (
                  <tr key={line.id}>
                    <td className={classes.index}>{line.lineNo}</td>
                    <td className={`${classes.wrap} ${classes.key}`}>{line.name}</td>
                    <td>{formatQuantity(line.quantity, locale)}</td>
                    <td>{line.unit}</td>
                    {prices ? (
                      <>
                        <td className={classes.nowrap}>{money(line.unitPrice)}</td>
                        <td className={classes.nowrap}>{money(line.total)}</td>
                      </>
                    ) : null}
                    {showReceived ? (
                      <>
                        <td>{formatQuantity(line.received, locale)}</td>
                        <td>{formatQuantity(line.open, locale)}</td>
                      </>
                    ) : null}
                  </tr>
                ))}
              </tbody>
              {prices ? (
                <tfoot>
                  {/* Kalemlerin toplamı sipariş tutarıdır; short close'da geçerli toplam ayrı (D8) */}
                  <ItemsTotalRow
                    label={amounts.shortClosed ? k('ordered') : k('total')}
                    amount={money(amounts.ordered)}
                    span={5}
                    trailing={showReceived ? 2 : 0}
                  />
                </tfoot>
              ) : null}
            </TableBox>
            {hiddenNote}
          </Tabs.Panel>

          <Tabs.Panel value="delivery" className={classes.tabBody}>
            <dl className={classes.facts}>
              <Fact label={k('facts.poNo')}>{detail.poNo}</Fact>
              <Fact label={k('facts.date')}>{formatDay(detail.orderDate)}</Fact>
              <Fact label={k('facts.vessel')}>{detail.vesselName}</Fact>
              <Fact label={k('facts.requisition')}>
                <LinkOr
                  label={detail.requisition.requisitionNo}
                  aria={k('openRequisition', { no: detail.requisition.requisitionNo })}
                  {...(onOpenRequisition
                    ? { onOpen: () => onOpenRequisition(detail.requisition.id) }
                    : {})}
                />
              </Fact>
              {rfq ? (
                <Fact label={k('facts.rfq')}>
                  <LinkOr
                    label={rfq.rfqNo}
                    aria={k('openRfq', { no: rfq.rfqNo })}
                    {...(onOpenRfq ? { onOpen: () => onOpenRfq(rfq.id) } : {})}
                  />
                </Fact>
              ) : null}
              <Fact label={k('facts.department')}>
                {departmentLabel(detail.requisition.department, t)}
              </Fact>
              <Fact label={k('facts.requestedBy')}>
                {detail.requisition.requestedByName ?? '—'}
              </Fact>
              <Fact label={k('expectedDelivery')}>{formatDay(detail.expectedDelivery)}</Fact>
              <Fact label={k('facts.deliveryLocation')}>{detail.deliveryLocation ?? '—'}</Fact>
              <Fact label={k('facts.deliveryTerms')}>{detail.deliveryTerms ?? '—'}</Fact>
              <Fact label={k('facts.paymentTerms')}>{detail.paymentTerms ?? '—'}</Fact>
              <Fact label={k('facts.warranty')}>{detail.warranty ?? '—'}</Fact>
              <Fact label={k('facts.supplierRef')}>{detail.supplierRef ?? '—'}</Fact>
              <Fact label={k('facts.contact')}>{detail.supplier.contactPerson ?? '—'}</Fact>
              <Fact label={k('facts.email')}>{detail.supplier.email ?? '—'}</Fact>
              <Fact label={k('facts.remarks')}>{detail.remarks ?? '—'}</Fact>
            </dl>
            {prices ? <PurchaseOrderTotals order={detail} /> : hiddenNote}
          </Tabs.Panel>

          <Tabs.Panel value="receipts" className={classes.tabBody}>
            {!hasReceipts ? (
              <EmptyState title={k('receiptsEmpty')} description={k('receiptsEmptyText')} />
            ) : (
              <TableBox label={k('tabs.receipts', { count: detail.receipts.length })}>
                <thead>
                  <tr>
                    <th scope="col" className={classes.key}>
                      {k('receiptColumns.grnNo')}
                    </th>
                    <th scope="col">{k('receiptColumns.date')}</th>
                    <th scope="col">{k('receiptColumns.receivedBy')}</th>
                    <th scope="col">{k('receiptColumns.deliveryNote')}</th>
                    <th scope="col" data-numeric>
                      {k('receiptColumns.accepted')}
                    </th>
                    <th scope="col">{k('receiptColumns.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.receipts.map((receipt) => (
                    <tr key={receipt.id}>
                      <td className={classes.key}>
                        <LinkOr
                          label={receipt.grnNo}
                          aria={receipt.grnNo}
                          {...(onOpenReceipt ? { onOpen: () => onOpenReceipt(receipt.id) } : {})}
                        />
                      </td>
                      <td>{formatDay(receipt.receivedDate)}</td>
                      <td>{receipt.receivedByName ?? '—'}</td>
                      <td>{receipt.deliveryNote ?? '—'}</td>
                      <td data-numeric>{formatQuantity(receipt.accepted, locale)}</td>
                      <td>
                        {receipt.cancelled ? (
                          <StatusBadge tone="neutral">{k('receiptCancelled')}</StatusBadge>
                        ) : (
                          <StatusBadge tone="success">{k('receiptValid')}</StatusBadge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </TableBox>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="history" className={classes.tabBody}>
            {detail.history.length === 0 ? (
              <EmptyState title={k('noHistory')} />
            ) : (
              <ul className={classes.comments}>
                {detail.history.map((event) => (
                  <li key={`${event.kind}-${event.at}`} className={classes.comment}>
                    <span className={classes.avatar} aria-hidden>
                      <IconCheck size={16} />
                    </span>
                    <div>
                      <p className={classes.commentRole}>{k(`events.${event.kind}`)}</p>
                      <p className={classes.commentText}>{historyDetail(event, t)}</p>
                    </div>
                    <span className={classes.commentAt}>{formatStampLocal(event.at)}</span>
                  </li>
                ))}
              </ul>
            )}
            {detail.emails.length > 0 ? (
              <>
                <h3 className={classes.subTitle}>{k('emails')}</h3>
                <TableBox label={k('emails')}>
                  <thead>
                    <tr>
                      <th scope="col" className={classes.key}>
                        {k('emailColumns.to')}
                      </th>
                      <th scope="col">{k('emailColumns.queuedAt')}</th>
                      <th scope="col">{k('emailColumns.status')}</th>
                      <th scope="col" data-numeric>
                        {k('emailColumns.attempts')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.emails.map((email) => (
                      <tr key={email.id}>
                        <td className={`${classes.wrap} ${classes.key}`}>{email.to}</td>
                        <td>{formatStampLocal(email.createdAt)}</td>
                        <td>
                          <StatusBadge
                            tone={
                              email.status === 'sent'
                                ? 'success'
                                : email.status === 'failed'
                                  ? 'danger'
                                  : 'neutral'
                            }
                          >
                            {k(`emailStatus.${email.status}`, { defaultValue: email.status })}
                          </StatusBadge>
                        </td>
                        <td data-numeric>{email.attempts}</td>
                      </tr>
                    ))}
                  </tbody>
                </TableBox>
              </>
            ) : null}
          </Tabs.Panel>
        </Tabs>
      </Panel>

      {!editor && source.scope === 'ship' ? <p className={classes.note}>{k('readOnly')}</p> : null}

      <div>
        <Button variant="subtle" onClick={onBack}>
          {k('back')}
        </Button>
      </div>

      {editor ? (
        <>
          <PasswordDialog
            errorScope="po"
            opened={dialog === 'submit'}
            title={k('submitTitle', { no: detail.poNo })}
            body={k('submitBody')}
            reason="none"
            password
            reauthKind="purchase_order"
            confirmLabel={k('submit')}
            onClose={() => setDialog(null)}
            onConfirm={({ password }) =>
              run(
                () =>
                  editor.submit(detail.id, {
                    rowVersion: detail.rowVersion,
                    ...(password ? { password } : {}),
                  }),
                (next) => k('notices.submitted', { no: next.poNo }),
              )
            }
          />
          <PasswordDialog
            errorScope="po"
            opened={dialog === 'approve'}
            title={k('approveTitle', { no: detail.poNo })}
            body={k('approveBody', {
              supplier: detail.supplier.name,
              total: `${money(detail.total)} ${detail.currency}`,
            })}
            reason="optional"
            password
            reauthKind="purchase_order"
            confirmLabel={k('approve')}
            onClose={() => setDialog(null)}
            onConfirm={({ reason, password }) =>
              run(
                () =>
                  editor.decide(detail.id, {
                    contentHash: detail.contentHash,
                    decision: 'approved',
                    reason: reason || null,
                    ...(password ? { password } : {}),
                  }),
                (next) => k('notices.approved', { no: next.poNo }),
              )
            }
          />
          <PasswordDialog
            errorScope="po"
            opened={dialog === 'return'}
            title={k('returnTitle', { no: detail.poNo })}
            body={k('returnBody')}
            reason="required"
            password
            reauthKind="purchase_order"
            confirmLabel={k('return')}
            onClose={() => setDialog(null)}
            onConfirm={({ reason, password }) =>
              run(
                () =>
                  editor.decide(detail.id, {
                    contentHash: detail.contentHash,
                    decision: 'returned',
                    reason,
                    ...(password ? { password } : {}),
                  }),
                (next) => k('notices.returned', { no: next.poNo }),
              )
            }
          />
          <PasswordDialog
            errorScope="po"
            opened={dialog === 'send'}
            title={k('sendTitle', { no: detail.poNo })}
            body={
              detail.supplier.email
                ? k('sendBody', { email: detail.supplier.email })
                : k('sendNoEmail')
            }
            reason="none"
            password={false}
            confirmLabel={k('send')}
            blocked={!sendConfirmable(detail)}
            onClose={() => setDialog(null)}
            onConfirm={() =>
              run(
                () => editor.send(detail.id),
                () => k('notices.queued'),
              )
            }
          />
          <PasswordDialog
            errorScope="po"
            opened={dialog === 'markSent'}
            title={k('markSentTitle', { no: detail.poNo })}
            body={k('markSentBody')}
            reason="none"
            password={false}
            confirmLabel={k('markSent')}
            onClose={() => setDialog(null)}
            onConfirm={() =>
              run(
                () => editor.markSent(detail.id, detail.rowVersion),
                (next) => k('notices.markedSent', { no: next.poNo }),
              )
            }
          />
          <PasswordDialog
            errorScope="po"
            opened={dialog === 'cancel'}
            title={k('cancelTitle', { no: detail.poNo })}
            body={k('cancelBody')}
            reason="required"
            password={false}
            confirmLabel={k('cancel')}
            onClose={() => setDialog(null)}
            onConfirm={({ reason }) =>
              run(
                () => editor.cancel(detail.id, { rowVersion: detail.rowVersion, reason }),
                (next) => k('notices.cancelled', { no: next.poNo }),
              )
            }
          />
          <PasswordDialog
            errorScope="po"
            opened={dialog === 'close'}
            title={k('closeTitle', { no: detail.poNo })}
            body={detail.status === 'delivered' ? k('closeBody') : k('closeShortBody')}
            reason={detail.status === 'delivered' ? 'optional' : 'required'}
            password={false}
            confirmLabel={k('close')}
            onClose={() => setDialog(null)}
            onConfirm={({ reason }) =>
              run(
                () =>
                  editor.close(detail.id, {
                    rowVersion: detail.rowVersion,
                    reason: reason || null,
                  }),
                (next) => k('notices.closed', { no: next.poNo }),
              )
            }
          />
          {dialog === 'edit' ? (
            <DeliveryDialog
              detail={detail}
              editor={editor}
              onClose={() => setDialog(null)}
              onSaved={(next) => {
                setDetail(next);
                setDialog(null);
                setNotice(k('notices.saved'));
              }}
            />
          ) : null}
          {dialog === 'confirm' ? (
            <ConfirmDialog
              detail={detail}
              editor={editor}
              onClose={() => setDialog(null)}
              onSaved={(next) => {
                setDetail(next);
                setDialog(null);
                setNotice(k('notices.confirmed'));
              }}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function LinkOr({ label, aria, onOpen }: { label: string; aria: string; onOpen?: () => void }) {
  if (!onOpen) return <>{label}</>;
  return (
    <a
      className={classes.link}
      href="?"
      aria-label={aria}
      onClick={(event) => {
        event.preventDefault();
        onOpen();
      }}
    >
      {label}
    </a>
  );
}

function TableBox({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section
      className={`${table.tableWrap} ${classes.boxed}`}
      aria-label={label}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
      tabIndex={0}
    >
      <table className={`${table.table} ${classes.table}`}>{children}</table>
    </section>
  );
}
